import api from './api';

/**
 * Real API implementation for production use
 * Handles data transformation from API format to app format
 */

// Transform API product data to app format
const transformProduct = (apiProduct) => {
  const defaultImage = apiProduct.images?.find(img => img.is_default) || apiProduct.images?.[0];
  const mainImage = defaultImage?.url || 'https://picsum.photos/400/400?random=1';

  // Extract different image sizes from the API structure - keep both webp and jpg
  const imageSizes = defaultImage?.sizes || {};
  const imageSm = {
    webp: imageSizes.large?.webp || mainImage,
    jpg: imageSizes.large?.jpg || mainImage
  }; // Use 660px (large) for small tiles
  const imageMd = {
    webp: imageSizes.large?.webp || mainImage,
    jpg: imageSizes.large?.jpg || mainImage
  }; // Use 660px (large) for medium/normal tiles
  const imageLg = {
    webp: imageSizes.large?.webp || mainImage,
    jpg: imageSizes.large?.jpg || mainImage
  };
  const imageXl = {
    webp: imageSizes.xlarge?.webp || mainImage,
    jpg: imageSizes.xlarge?.jpg || mainImage
  };
  const imageThumbnail = {
    webp: imageSizes.thumbnail?.webp || mainImage,
    jpg: imageSizes.thumbnail?.jpg || mainImage
  };

  const category = apiProduct.collections?.[0]?.name || 'Produtos';

  // Build specifications from attributes and other fields
  const specifications = {};
  if (apiProduct.attributes?.length > 0) {
    apiProduct.attributes.forEach(attr => {
      specifications[attr.name] = attr.value;
    });
  }
  if (apiProduct.weight) {
    specifications.weight = `${apiProduct.weight}g`;
  }
  if (apiProduct.volume) {
    specifications.volume = `${apiProduct.volume}`;
  }

  return {
    id: apiProduct.id,
    sku: apiProduct.sku,
    name: apiProduct.name,
    category: category,
    collection: apiProduct.collections?.[0]?.name || '',
    collections: apiProduct.collections || [],
    image: mainImage,
    imageSm: imageSm,
    imageMd: imageMd,
    imageLg: imageLg,
    imageXl: imageXl,
    imageThumbnail: imageThumbnail,
    images: apiProduct.images?.map(img => img.url) || [mainImage],
    imagesFull: apiProduct.images || [], // Keep full image objects with sizes
    description: apiProduct.description || apiProduct.summary || '',
    summary: apiProduct.summary || '',
    material: apiProduct.material || 'Ouro 18k',
    specifications: specifications,
    availability: apiProduct.in_stock ? 'Disponível' : 'Sob encomenda',
    in_stock: apiProduct.in_stock || false,
    quantity: apiProduct.quantity || 0,
    isNew: apiProduct.is_new || false,
    price: apiProduct.price,
    original_price: apiProduct.original_price,
    currency: apiProduct.currency || 'EUR',
    discount_percentage: apiProduct.discount_percentage || 0,
    allow_purchase_out_of_stock: apiProduct.allow_purchase_out_of_stock || false,
    variants: apiProduct.variants || [],
    inventory: apiProduct.inventory || [],
    url: apiProduct.url || '',
    weight: apiProduct.weight || 0,
    volume: apiProduct.volume || 0
  };
};

const productService = {
  // Get all products with optional filters
  async getProducts(filters = {}) {
    try {
      const response = await api.get('/products/', { 
        params: filters,
        cacheTTL: 5 * 60 * 1000, // Cache for 5 minutes
        cache: !filters.noCache // Allow disabling cache with noCache flag
      });

      // Handle the nested data structure from the API
      let products = [];
      if (response.data.success) {
        if (response.data.data?.products && Array.isArray(response.data.data.products)) {
          // Handle paginated response
          products = response.data.data.products;
        } else if (Array.isArray(response.data.data)) {
          // Handle direct array response
          products = response.data.data;
        }

        const transformedProducts = products.map(transformProduct);
        return {
          ...response,
          data: transformedProducts
        };
      }

      // Return empty array if no success or data
      return {
        ...response,
        data: []
      };
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  },

  // Get single product by ID
  async getProductById(id) {
    try {
      const response = await api.get(`/products/${id}/`, {
        cacheTTL: 10 * 60 * 1000 // Cache for 10 minutes
      });

      // Transform the API response
      if (response.data.success && response.data.data) {
        const transformedProduct = transformProduct(response.data.data);
        return {
          ...response,
          data: transformedProduct
        };
      }

      return response;
    } catch (error) {
      console.error('Error fetching product:', error);
      throw error;
    }
  },

  // Get product categories
  async getCategories() {
    try {
      const response = await api.get('/collections/', {
        cacheTTL: 30 * 60 * 1000 // Cache for 30 minutes (categories don't change often)
      });

      // Ensure the response is in the expected format
      if (response.data.success && response.data.data) {
        return {
          ...response,
          data: response.data.data
        };
      }

      return response;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  },

  // Get featured products for homepage
  // Note: Uses the main products endpoint since /featured/ doesn't exist
  async getFeaturedProducts() {
    try {
      // Get first 8 products as featured (you can adjust filters as needed)
      const response = await api.get('/products/', {
        params: {
          limit: 8,
          // Add any filters for featured products if needed
          // e.g., is_new: true, or specific collections
        },
        cacheTTL: 10 * 60 * 1000 // Cache for 10 minutes
      });

      // Handle the nested data structure from the API
      let products = [];
      if (response.data.success) {
        if (response.data.data?.products && Array.isArray(response.data.data.products)) {
          // Handle paginated response
          products = response.data.data.products;
        } else if (Array.isArray(response.data.data)) {
          // Handle direct array response
          products = response.data.data;
        }

        const transformedProducts = products.map(transformProduct);
        return {
          ...response,
          data: transformedProducts
        };
      }

      return response;
    } catch (error) {
      console.error('Error fetching featured products:', error);
      throw error;
    }
  },

  // Get collections
  async getCollections() {
    try {
      const response = await api.get('/collections/', {
        cacheTTL: 30 * 60 * 1000 // Cache for 30 minutes (collections don't change often)
      });

      // Return the collections data structure as-is
      if (response.data.success && response.data.data) {
        return {
          ...response,
          data: response.data.data.collections || []
        };
      }

      return response;
    } catch (error) {
      console.error('Error fetching collections:', error);
      throw error;
    }
  },

  // Cache management utilities
  clearCache() {
    return import('./cacheService').then(module => {
      const cacheService = module.default;
      cacheService.clear();
      console.log('Cache cleared');
    });
  },

  clearProductCache() {
    return import('./cacheService').then(module => {
      const cacheService = module.default;
      cacheService.clearByPattern('/products');
      console.log('Product cache cleared');
    });
  },

  getCacheStats() {
    return import('./cacheService').then(module => {
      const cacheService = module.default;
      return cacheService.getStats();
    });
  }
};

export default productService;